import { Center, CenterProps, Divider, Flex } from "styled-system/jsx";
import {
  useReservationDetails,
  UseReservationDetailsProps,
} from "./useReservationDetails";
import { SurveryResponses } from "./components/SurveryResponses";
import { Waivers } from "./components/Waivers";
import { Receipt } from "./components/Receipt";
import { Guests } from "./components/Guests";
import { PurchaseDetails } from "./components/PurchaseDetails";
import { ThankYou } from "./components/ThankYou";
import { ReservationHeader } from "./components/ReservationHeader";
import { GettingHere } from "./components/GettingHere";
import { ReservationLoading } from "./components/ReservationLoading";
import { ReservationError } from "./components/ReservationError";
import { ReservationInfoCard } from "./components/ReservationInfoCard";
import { MerchDeliveryStatusCard } from "./components/MerchDeliveryStatusCard";
import { DeliveryDetails } from "./components/DeliveryDetails";
import { useTranslation } from "react-i18next";
import { Refunds } from "./components/Refunds";
import { FailedScheduledPayment } from "./components/FailedScheduledPayment/FailedScheduledPayment";
import { StripeElementsWrapper } from "widgets/booker/shared";
import { getTotalGuestCount } from "widgets/booker/shared/utils";
import { EnumProductType } from "gql/generated";
import {
  formatDate,
  isEftPendingChargeId,
  isAchPendingChargeId,
} from "shared/utils";
import { resolveLegacyReceiptPdfUrl } from "shared/utils/legacySilkstartPdf";
import { Button, Text } from "@ttc3k/trekker";
import { useSearchParams } from "react-router";
import { buildPurchaseDetailsTierVariant } from "./components/resolveOrderReceiptLines";

export const ReservationDetails = ({
  orderId,
  isPublic,
  hidePaymentRetry,
  preloadedOrder,
  preloadedRefunds,
  preloadedFailedScheduledPayment,
  ...props
}: UseReservationDetailsProps & { hidePaymentRetry?: boolean } & CenterProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS",
  });
  const [searchParams] = useSearchParams();
  const printMode = searchParams.get("print") === "1";
  const {
    order,
    booker,
    orderPrices,
    refunds,
    failedScheduledPayment,
    renewablePurchase,
    loading,
    error,
    productTypeConfig,
    contactInfo,
    mainProduct,
    gettingHereAddress,
    purchaserBusinessName,
  } = useReservationDetails({
    orderId,
    isPublic,
    preloadedOrder,
    preloadedRefunds,
    preloadedFailedScheduledPayment,
  });

  if (loading) {
    return <ReservationLoading {...props} />;
  }
  if (error) {
    return <ReservationError error={error} orderId={orderId} {...props} />;
  }

  const tierVariant =
    order != null
      ? buildPurchaseDetailsTierVariant(order, orderPrices, renewablePurchase)
      : null;

  const isMerchOrder = mainProduct?.type === EnumProductType.Merch;
  const isFeeOrRenewableFeeOrder =
    mainProduct?.type === EnumProductType.Fee ||
    mainProduct?.type === EnumProductType.RenewableFee;
  const fulfillment = (order as any)?.fulfillment as
    | { status?: string | null; note?: string | null }
    | undefined;

  const chargeId = order?.stripeMetadata?.chargeId ?? "";
  const isEftPending = isEftPendingChargeId(chargeId);
  const isAchPending = isAchPendingChargeId(chargeId);

  const canDownloadReceipt =
    !!order && !order.cancelled && !isEftPending;

  return (
    <Center
      w="100%"
      h="100%"
      {...(order ? { "data-order-print-ready": true } : {})}
      {...props}
    >
      <Flex flexDir="column" alignItems="center" gap="400">
        <ReservationHeader booker={booker} contactInfo={contactInfo} />
        <Divider />

        {isEftPending && (
          <ReservationInfoCard
            visual="highlight"
            title={
              isAchPending
                ? t("ACH_PENDING_PUBLIC.TITLE")
                : t("EFT_PENDING_PUBLIC.TITLE")
            }
            description={
              isAchPending
                ? t("ACH_PENDING_PUBLIC.DESCRIPTION")
                : t("EFT_PENDING_PUBLIC.DESCRIPTION")
            }
          />
        )}

        <Refunds
          orderAmount={orderPrices?.grandTotal ?? 0}
          refunds={refunds}
          contactInfo={contactInfo}
        />

        {!hidePaymentRetry && (
          <StripeElementsWrapper
            connectedAccountId={
              failedScheduledPayment?.stripeMetadata?.connectedAccountId ??
              booker?.paymentMetadata?.connectedAccountId ??
              ""
            }
            stripeRegion={booker?.paymentMetadata?.stripeRegion ?? "CA"}
            stripeTestMode={!!order?.testMode}
          >
            <FailedScheduledPayment
              failedScheduledPayment={failedScheduledPayment}
              stripeRegion={
                (booker?.paymentMetadata?.stripeRegion ?? "CA") as "CA" | "US"
              }
              optionalCardPostalCode={
                !!booker?.paymentMetadata?.optionalCardPostalCode
              }
            />
          </StripeElementsWrapper>
        )}

        {/* CANCELLED INFO */}
        {order?.cancelled && (
          <ReservationInfoCard
            visual="error"
            description={
              productTypeConfig?.orderTermLowercase === "reservation"
                ? t("CANCELLED.DESCRIPTION")
                : t("CANCELLED.DESCRIPTION_ORDER")
            }
          />
        )}

        {mainProduct?.type === EnumProductType.Fee &&
          fulfillment?.status === "FULFILLED" &&
          fulfillment.note && (
            <ReservationInfoCard
              visual="success"
              title={t("FULFILLMENT.TITLE")}
              description={fulfillment.note}
            />
          )}

        {order && !order.cancelled && <MerchDeliveryStatusCard order={order} />}

        <Flex
          flexDir="column"
          alignItems="center"
          gap="400"
          w="full"
          opacity={order?.cancelled ? 0.5 : 1}
        >
          <ThankYou
            orderId={orderId}
            purchaseDate={order?.createdDateISO}
            productTypeConfig={productTypeConfig}
            supportEmail={contactInfo?.email ?? ""}
          />

          {renewablePurchase &&
            renewablePurchase.status !== "CANCELLED" &&
            renewablePurchase.status !== "EXPIRED" &&
            !isEftPending && (
              <ReservationInfoCard
                visual="accent.blue"
                title={t("SUBSCRIPTION.TITLE")}
                description={
                  t("SUBSCRIPTION.RENEWS_ON", {
                    date: formatDate(
                      renewablePurchase.currentPeriodEnd,
                      "MMMM dd, yyyy",
                    ),
                  })
                }
              />
            )}

          {renewablePurchase &&
            renewablePurchase.status !== "CANCELLED" &&
            renewablePurchase.status !== "EXPIRED" &&
            isEftPending && (
              <ReservationInfoCard
                visual="accent.blue"
                title={t("SUBSCRIPTION.TITLE")}
                description={
                  t("SUBSCRIPTION.PENDING_RENEWS_ON", {
                    date: formatDate(
                      renewablePurchase.currentPeriodEnd,
                      "MMMM dd, yyyy",
                    ),
                  })
                }
              />
            )}

          {renewablePurchase &&
            renewablePurchase.status === "CANCELLED" &&
            renewablePurchase.cancelledAt && (
              <ReservationInfoCard
                visual="highlight"
                description={
                  t("SUBSCRIPTION.CANCELLED_INFO", {
                    date: formatDate(
                      renewablePurchase.cancelledAt,
                      "MMMM dd, yyyy",
                    ),
                    endDate: formatDate(
                      renewablePurchase.currentPeriodEnd,
                      "MMMM dd, yyyy",
                    ),
                  })
                }
              />
            )}

          {renewablePurchase &&
            renewablePurchase.status !== "CANCELLED" &&
            renewablePurchase.status !== "EXPIRED" &&
            !order?.cancelled &&
            !isEftPending && (
              <Text
                visual="smallRegular"
                color="text.accent.blue.mid"
                cursor="pointer"
                textDecoration="underline"
                onClick={() =>
                  window.open(
                    `mailto:${contactInfo?.email ?? ""}?subject=Cancel subscription – Order ${orderId}`,
                    "_blank",
                  )
                }
              >
                {t("SUBSCRIPTION.CANCEL_LINK")}
              </Text>
            )}

          <Divider />
          <PurchaseDetails
            purchasedProducts={order?.purchasedProducts ?? []}
            guestCount={getTotalGuestCount(
              order?.purchasedProducts[0]?.guestsPerTier ?? [],
            )}
            productTypeConfig={productTypeConfig}
            tierVariant={tierVariant}
          />
          {order?.delivery && <DeliveryDetails order={order} />}
          <Guests
            guests={order?.guests ?? []}
            email={order?.customer?.email ?? ""}
            name={
              order?.customer
                ? order.customer.firstName + " " + order.customer.lastName
                : ""
            }
            phone={order?.customer?.phone ?? undefined}
            address={order?.customer?.address}
            businessName={
              isFeeOrRenewableFeeOrder ? purchaserBusinessName : undefined
            }
            purchaserHeading={isFeeOrRenewableFeeOrder}
          />
          <Receipt
            order={order}
            orderPrices={orderPrices}
            orderPaymentMetadata={order?.paymentMetadata}
            renewablePurchase={renewablePurchase}
          />
          <Waivers waivers={booker?.waivers ?? []} />
          <SurveryResponses
            surveyResponse={order?.surveyResponse ?? { responses: [] }}
          />
          {!isFeeOrRenewableFeeOrder && !isMerchOrder && (
            <GettingHere address={gettingHereAddress ?? {}} />
          )}
        </Flex>

        {!printMode && canDownloadReceipt && (
          <Button
            visual="primary"
            mode="light"
            size="lg"
            w="full"
            onClick={() =>
              window.open(
                resolveLegacyReceiptPdfUrl({
                  orderId,
                  stripeInvoiceId: order?.stripeMetadata?.invoiceId,
                }) ??
                  `${import.meta.env.VITE_API_SERVER_URL}/order/${orderId}/pdf`,
                "_blank",
              )
            }
          >
            {t("DOWNLOAD_RECEIPT")}
          </Button>
        )}
      </Flex>
    </Center>
  );
};
